import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { modal } from '@/helpers/ui';
import { useRegisterOrderStore } from '@/modules/register/stores/order';
import { useRegisterStore } from '@/modules/register/stores/register';
import { useRegisterDrawerStore } from '@/stores/register-drawer';
import { useUserStore } from '@/stores/user';
import { mutateOrder } from '../actions/order';

export const usePaymentSubmit = () => {
  const showCheckoutOverviewModal = ref(false);

  const { order, reset } = useRegisterOrderStore();
  const { cannotSaveOutstanding, correction } = storeToRefs(
    useRegisterOrderStore(),
  );
  const {
    cashupEnabled,
    showOpenDrawerModal,
    savingOrder,
    showCannotSaveOutstandingModal,
  } = storeToRefs(useRegisterStore());
  const { isOpen: isDrawerOpen, isLoadingDrawerStatus } = storeToRefs(
    useRegisterDrawerStore(),
  );
  const { t } = useI18n();
  const { hasFeatureFlag } = useUserStore();
  const { call: callOrderMutation, onDone: onDoneMutation } = mutateOrder();
  const savedOrderId = ref<string | null>(null);
  const invoiceJustIssued = ref(false);

  const sendPayload = () => {
    savingOrder.value = true;
    invoiceJustIssued.value = !order.hasInvoice;
    if (order.id && !order.originalOrderId) {
      order.draft = false;
      if (order.transactions.length > 0) {
        order.pending = false;
      }
      callOrderMutation('update', {
        withCustomerResponse: true,
        correction: correction.value,
      });
    } else {
      callOrderMutation('create', { withCustomerResponse: true });
    }
  };

  const submit = () => {
    if (savingOrder.value) return;
    if (cannotSaveOutstanding.value && hasFeatureFlag('compliant-register')) {
      showCannotSaveOutstandingModal.value = true;
      return;
    }
    if (isLoadingDrawerStatus.value) return;
    if (cashupEnabled.value && !isDrawerOpen.value) {
      showOpenDrawerModal.value = true;
    } else {
      sendPayload();
    }
  };

  const errorMessage = (errors: any) => {
    if (errors.location_id) {
      return errors.location_id[0];
    }
    if (errors['items.prepaid_card']) {
      return t('prepaid_card.no_funds');
    }
    if (errors['items.appointment_part']) {
      return t('register.appointment_paid');
    }
    if (errors['items.base']) {
      return t('register.items_cashup_error');
    }
    if (errors['transactions.giftcard']) {
      return t('register.giftcard_error');
    }
    if (errors['transactions.gifty']) {
      return errors['transactions.gifty']; // not translated because it comes from Gifty
    }
    return t('register.general_error');
  };

  const handleResponse = ({ order: savedOrder, errors }: any) => {
    if (errors) {
      modal('warning', { message: errorMessage(errors) });
      savingOrder.value = false;
      return;
    }
    if (savedOrder) {
      showCheckoutOverviewModal.value = true;
      savedOrderId.value = savedOrder.id;
      savingOrder.value = false;
      reset();
    }
  };

  onDoneMutation(({ data }) => {
    if (data) {
      if (data.updateOrder?.errors?.record_not_destroyed) {
        modal('warning', { message: t('register.items_cashup_error') });
        savingOrder.value = false;
      } else {
        handleResponse(data.createOrder || data.updateOrder);
      }
    } else {
      savingOrder.value = false;
    }
  });

  return {
    submit,
    sendPayload,
    showCheckoutOverviewModal,
    savedOrderId,
    invoiceJustIssued,
  };
};
